// src/pages/AuthorsDetailPage.tsx
// 新 API 移行版:
//   - 著者情報 + 著作一覧: /graph の GetAuthorityGraphOne（display 済み・rl_book_id ベース）
//   - Wikipedia リンク・概要: 旧 AUTHOR_DETAIL（P7 で新 API 化するまで暫定。失敗しても本体表示は継続）

import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { NEW_API, LEGACY_API } from '../config/api';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import IosShareIcon from '@mui/icons-material/IosShare';
import LinkIcon from '@mui/icons-material/Link';

import { generateCoverUrlSmart } from '../utils/generateCoverUrl';
import { titleWithVolume } from '../utils/bibAdapter';
import { useAppTokens } from '../context/ThemeModeContext';
import wikipediaLogo from '../assets/images/wikipedia_logo.png';
import ndlAuthoritiesBanner from '../assets/images/ndl_authorities_banner.png';

// GetAuthorityGraphOne の book ノードを出版年（pubYear）降順にソート
const sortBooksByPubYear = (books: any[]) => {
  return [...books].sort((a, b) => (b.pubYear ?? 0) - (a.pubYear ?? 0));
};

// rl_book_id でユニークな配列を作成する関数
const filterUniqueBooks = (books: any[]) => {
  const seen = new Set<number>();
  return books.filter((book) => {
    if (seen.has(book.rl_book_id)) return false;
    seen.add(book.rl_book_id);
    return true;
  });
};

const AuthorsDetailPage: React.FC = () => {
  const { tenkyo_id } = useParams<{ tenkyo_id: string }>();
  const navigate = useNavigate();

  const [authorNode, setAuthorNode] = useState<any | null>(null);
  const [books, setBooks] = useState<any[]>([]);
  const [aliases, setAliases] = useState<any[]>([]);
  const [wikipediaLink, setWikipediaLink] = useState<string | null>(null);
  const [wikipediaSummary, setWikipediaSummary] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const TOKENS = useAppTokens();

  const glassPanelSx = {
    background: TOKENS.glass,
    border: `1px solid ${TOKENS.border}`,
    borderRadius: 2,
    boxShadow: '0 0 0 rgba(0,0,0,0)',
    backdropFilter: 'blur(6px)',
  } as const;

  const pillSx = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 1,
    px: 1.25,
    py: 0.75,
    borderRadius: 999,
    background: TOKENS.glassStrong,
    border: `1px solid ${TOKENS.border}`,
    color: TOKENS.text,
    fontSize: 12,
    lineHeight: 1,
    userSelect: 'none',
  } as const;

  const ghostButtonSx = {
    color: TOKENS.text,
    background: TOKENS.buttonBg,
    border: `1px solid ${TOKENS.borderSoft}`,
    borderRadius: 999,
    textTransform: 'none',
    fontSize: 12,
    px: 1.25,
    py: 0.75,
    minHeight: 34,
    '&:hover': {
      background: TOKENS.buttonBgHover,
      border: `1px solid ${TOKENS.accent}`,
    },
  } as const;

  const iconGhostButtonSx = {
    color: TOKENS.text,
    background: TOKENS.buttonBg,
    border: `1px solid ${TOKENS.borderSoft}`,
    '&:hover': {
      background: TOKENS.buttonBgHover,
      border: `1px solid ${TOKENS.accent}`,
    },
  } as const;

  // 外部ロゴ画像（Wikipedia）用。ロゴ自体に既に色/デザインがあるため、
  // 通常のボタンのような枠・背景で囲むと違和感がある。枠・背景なし
  const logoLinkButtonSx = {
    p: 0.5,
    borderRadius: 2,
    minHeight: 'unset',
    minWidth: 'unset',
    background: 'transparent',
    border: '1px solid transparent',
    '&:hover': {
      background: 'transparent',
      border: '1px solid transparent',
    },
  } as const;

  const bookThumbCardSx = {
    transition: '0.25s',
    background: TOKENS.cardBg,
    border: `1px solid ${TOKENS.border}`,
    borderRadius: 2,
    overflow: 'hidden',
    cursor: 'pointer',
    boxShadow: 'none',
    '&:hover': {
      border: `1px solid ${TOKENS.accent}`,
      background: TOKENS.cardBgHover,
    },
  } as const;

  useEffect(() => {
    if (!tenkyo_id) return;

    // 著者情報 + 著作一覧 + 別名一覧（新 API — /graph の GetAuthorWithAliases）
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await axios.get(NEW_API.GRAPH_ENGINE, {
          params: { operation: 'GetAuthorWithAliases', tenkyo_id },
        });
        const data = res.data?.data;
        if (!data?.author) {
          setError('著者情報の取得に失敗しました。');
          return;
        }
        setAuthorNode(data.author);
        setBooks(data.books ?? []);
        setAliases(data.aliases ?? []);
      } catch (e) {
        console.error(e);
        setError('著者情報の取得に失敗しました。');
      } finally {
        setLoading(false);
      }
    };

    // Wikipedia リンク・概要（旧 API — P7 で新 API 化するまで暫定。失敗しても無視）
    const fetchWikipedia = async () => {
      try {
        const res = await axios.get(`${LEGACY_API.AUTHOR_DETAIL}/${tenkyo_id}`);
        setWikipediaLink(res.data?.dynamodb_item?.Wikipedia_Link ?? null);
        setWikipediaSummary(res.data?.wikipedia_summary ?? '');
      } catch {
        /* Wikipedia 情報はオプショナルなので握りつぶす */
      }
    };

    fetchData();
    fetchWikipedia();
  }, [tenkyo_id]);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const sortedBooks = useMemo(() => sortBooksByPubYear(filterUniqueBooks(books)), [books]);

  const authorName = authorNode?.name ? authorNode.name.replace(/,/g, '') : '不明';
  const authorPeriod = authorNode?.dates ?? '';

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(`https://readlinker.com/author/${tenkyo_id}`);
      alert('リンクをコピーしました');
    } catch (error) {
      console.error('Error copying link:', error);
    }
  };

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: authorName,
          url: `https://readlinker.com/author/${tenkyo_id}`,
        });
      } else {
        navigator.clipboard.writeText(`https://readlinker.com/author/${tenkyo_id}`);
        alert('URLをコピーしました');
      }
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  const getBookCoverUrl = (book: any) =>
    generateCoverUrlSmart(book.rl_book_id, book.title, book.isbn13);

  const getBookTitle = (book: any) => titleWithVolume(book.title ?? '', book.volume);
  const getBookDate = (book: any) => (book.pubYear != null ? String(book.pubYear) : '');
  const getBookKey = (book: any) => book.rl_book_id;

  const handleBookClick = (book: any) => {
    if (book.rl_book_id) {
      navigate(`/book/${book.rl_book_id}`);
      window.scrollTo(0, 0);
    }
  };

  if (loading) {
    return (
      <Box sx={{ width: '100vw', minHeight: '100dvh', background: `radial-gradient(1200px 600px at 20% 10%, ${TOKENS.radialGlow}, transparent 60%), ${TOKENS.bg}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: TOKENS.text }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ width: '100vw', minHeight: '100dvh', background: `radial-gradient(1200px 600px at 20% 10%, ${TOKENS.radialGlow}, transparent 60%), ${TOKENS.bg}`, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2, color: TOKENS.text }}>
        <Typography sx={{ fontSize: 13, color: TOKENS.error }}>{error}</Typography>
      </Box>
    );
  }

  if (!authorNode) return null;

  return (
    <Box sx={{ mt: 7 }}>
      <Box
        sx={{
          width: '100%', minHeight: 'calc(100dvh - 56px)',
          background: `radial-gradient(1200px 600px at 20% 10%, ${TOKENS.radialGlow}, transparent 60%), ${TOKENS.bg}`,
          color: TOKENS.text, position: 'relative',
          overflowY: { xs: 'auto', md: 'hidden' },
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <Container maxWidth={false} sx={{ pt: 3, pb: 3, px: { xs: 2, md: 3 }, height: { xs: 'auto', md: '100%' } }}>
          <Grid container spacing={2} sx={{ height: { xs: 'auto', md: '100%' } }}>
            {/* Left: Author overview */}
            <Grid item xs={12} md={5} sx={{ height: { xs: 'auto', md: '100%' } }}>
              <Box sx={{ ...glassPanelSx, height: { xs: 'auto', md: 'calc(100dvh - 64px)' }, display: 'flex', flexDirection: 'column', overflow: { xs: 'visible', md: 'hidden' } }}>
                <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${TOKENS.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                  <Typography sx={{ fontSize: 20, fontWeight: 700, color: TOKENS.text }}>著者詳細</Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {wikipediaLink && (
                      <Button
                        href={wikipediaLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={logoLinkButtonSx}
                      >
                        <img src={wikipediaLogo} alt="Wikipedia" style={{ width: 'auto', height: '22px' }} />
                      </Button>
                    )}
                    {tenkyo_id && (
                      <Button
                        href={`https://id.ndl.go.jp/auth/ndlna/${tenkyo_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={logoLinkButtonSx}
                      >
                        <img src={ndlAuthoritiesBanner} alt="Web NDL Authorities" style={{ width: 'auto', height: '22px' }} />
                      </Button>
                    )}
                    <IconButton onClick={handleCopyLink} sx={{ ...iconGhostButtonSx, borderRadius: 999, width: 34, height: 34 }}>
                      <LinkIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                    <IconButton onClick={handleShare} sx={{ ...iconGhostButtonSx, borderRadius: 999, width: 34, height: 34 }}>
                      <IosShareIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Stack>
                </Box>

                <Box sx={{ px: 2, py: 2, overflowY: { xs: 'visible', md: 'auto' }, WebkitOverflowScrolling: 'touch', flex: { xs: 'unset', md: 1 }, minHeight: 0 }}>
                  <Typography component="h1" sx={{ fontSize: 20, lineHeight: 1.2, color: TOKENS.text, letterSpacing: '0.2px', mb: 1 }}>
                    {authorName}
                  </Typography>

                  {authorPeriod && (
                    <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub, mb: 2 }}>{authorPeriod}</Typography>
                  )}

                  {wikipediaSummary ? (
                    <Box sx={{ mt: 1 }}>
                      <Typography sx={{ fontSize: 13, color: TOKENS.text, whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>{wikipediaSummary}</Typography>
                      <Typography sx={{ fontSize: 12, color: TOKENS.textSub, mt: 1 }}>（Wikipediaより）</Typography>
                    </Box>
                  ) : (
                    <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub, mt: 2 }}>概要は未取得</Typography>
                  )}

                  <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${TOKENS.border}` }}>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Box sx={pillSx}>著者の本: {sortedBooks.length}</Box>
                      {aliases.length > 0 && (
                        <Box sx={pillSx}>別名: {aliases.length}</Box>
                      )}
                    </Stack>
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* Right: Books */}
            <Grid item xs={12} md={7} sx={{ height: { xs: 'auto', md: '100%' } }}>
              <Box sx={{ ...glassPanelSx, height: { xs: 'auto', md: 'calc(100dvh - 64px)' }, display: 'flex', flexDirection: 'column', overflow: { xs: 'visible', md: 'hidden' } }}>
                <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${TOKENS.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                  <Typography sx={{ fontSize: 13, color: TOKENS.textSub }}>書籍一覧</Typography>
                  <Box sx={{ ...pillSx, opacity: 0.9 }}>最新順</Box>
                </Box>

                <Box sx={{ px: 2, py: 2, overflowY: { xs: 'visible', md: 'auto' }, WebkitOverflowScrolling: 'touch', flex: { xs: 'unset', md: 1 }, minHeight: 0 }}>
                  {sortedBooks.length > 0 && (
                    <Box sx={{ mb: 3, pb: 2 }}>
                      <Typography sx={{ fontSize: 16, color: TOKENS.text, mb: 1.5 }}>{authorName} の本</Typography>
                      <Grid container spacing={1.5} sx={{ m: 0, width: '100%' }}>
                        {sortedBooks.map((book: any) => (
                          <Grid item xs={6} sm={4} md={3} key={getBookKey(book)}>
                            <Box>
                              <Card onClick={() => handleBookClick(book)} sx={bookThumbCardSx}>
                                <CardMedia
                                  component="img"
                                  image={getBookCoverUrl(book)}
                                  alt={`Cover for ${getBookTitle(book)}`}
                                  sx={{ objectFit: 'contain', height: 190, width: '100%', padding: '10px', background: TOKENS.cardBg }}
                                />
                              </Card>
                              <Box sx={{ mt: 1 }}>
                                <Typography sx={{ fontSize: 12.5, color: TOKENS.text, lineHeight: 1.25, display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, overflow: 'hidden' }}>
                                  {getBookTitle(book)}
                                </Typography>
                                <Typography sx={{ fontSize: 12, color: TOKENS.textSub, mt: 0.25 }}>{getBookDate(book)}</Typography>
                              </Box>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}

                  {sortedBooks.length === 0 && aliases.length === 0 && (
                    <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub }}>書籍が見つかりませんでした</Typography>
                  )}

                  {/* 別名（本名・筆名・芸名・号 等）ごとに、著者情報と著作一覧をフィールドとして表示 */}
                  {aliases.map((alias: any) => {
                    const aliasBooks = sortBooksByPubYear(filterUniqueBooks(alias.books ?? []));
                    const aliasName = alias.author?.name ? String(alias.author.name).replace(/,/g, '') : '不明';
                    return (
                      <Box
                        key={alias.tenkyoId}
                        sx={{ mb: 3, pb: 2, pt: 2, borderTop: `1px solid ${TOKENS.border}` }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                          <Box sx={{ ...pillSx, opacity: 0.9 }}>{alias.relationLabel ?? '別名'}</Box>
                          <Typography
                            component="a"
                            onClick={() => {
                              navigate(`/author/${alias.tenkyoId}`);
                              window.scrollTo(0, 0);
                            }}
                            sx={{ fontSize: 16, color: TOKENS.text, cursor: 'pointer', '&:hover': { color: TOKENS.accent } }}
                          >
                            {aliasName}
                          </Typography>
                        </Stack>
                        {aliasBooks.length > 0 ? (
                          <Grid container spacing={1.5} sx={{ m: 0, width: '100%' }}>
                            {aliasBooks.map((book: any) => (
                              <Grid item xs={6} sm={4} md={3} key={getBookKey(book)}>
                                <Box>
                                  <Card onClick={() => handleBookClick(book)} sx={bookThumbCardSx}>
                                    <CardMedia
                                      component="img"
                                      image={getBookCoverUrl(book)}
                                      alt={`Cover for ${getBookTitle(book)}`}
                                      sx={{ objectFit: 'contain', height: 190, width: '100%', padding: '10px', background: TOKENS.cardBg }}
                                    />
                                  </Card>
                                  <Box sx={{ mt: 1 }}>
                                    <Typography sx={{ fontSize: 12.5, color: TOKENS.text, lineHeight: 1.25, display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, overflow: 'hidden' }}>
                                      {getBookTitle(book)}
                                    </Typography>
                                    <Typography sx={{ fontSize: 12, color: TOKENS.textSub, mt: 0.25 }}>{getBookDate(book)}</Typography>
                                  </Box>
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                        ) : (
                          <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub }}>書籍が見つかりませんでした</Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default AuthorsDetailPage;
